import React, { useRef } from 'react';
import ModalLayoutSelection from '../ModalLayoutSelection';
import styles from './AddressInformationModal.scss';
import AddressInformation from '../AddressInformation';
import AddressInformationType from '../AddressInformation/AddressInformation';
import dataHooks from '../../data-hooks';
import { useBi, useTranslation } from '@wix/yoshi-flow-editor';
import ModalLayoutBasicFooter from '../ModalLayoutBasicFooter';
import { ChangeLocationPayload } from '../../../../state/session/session.actions.types';
import { ActionCreator } from 'redux';
import Spinner from '../Spinner';
import classNames from 'classnames';
import { closeDispatchSettings } from '@wix/bi-logger-olo-client/v2';

export interface AddressInformationModalProps {
  isMobile: boolean;
  onRequestClose: () => void;
  isMultiLocation?: boolean;
  isFormValid: boolean;
  changeLocation: (payload: ChangeLocationPayload) => void;
  revertAddress: ActionCreator<void>;
  isLoadingLocationFromServer: boolean;
  isLoadingLocationsSettingsFromServer: boolean;
  isAddressSelectionModalOpen: boolean;
  isFetchingEstimateFromTPA?: boolean;
}
type AddressInformationRef = React.ElementRef<typeof AddressInformationType>;

const AddressInformationModal: React.FC<AddressInformationModalProps> = ({
  isMobile,
  onRequestClose,
  isMultiLocation,
  changeLocation,
  isFormValid,
  isLoadingLocationFromServer,
  isLoadingLocationsSettingsFromServer,
  isAddressSelectionModalOpen,
  revertAddress,
  isFetchingEstimateFromTPA,
}) => {
  const { t } = useTranslation();
  const addressInformationRef = useRef<AddressInformationRef>(null);
  const biLogger = useBi();
  const isIphone = typeof navigator !== 'undefined' && navigator.platform === 'iPhone';

  function handleOkClick() {
    // we are calling the internal submit method of AddressInformation component so we don't have to duplicate it.
    // this code should be deprecated once we close the multi location experiment

    // eslint-disable-next-line no-unused-expressions
    addressInformationRef.current?.submit();
  }

  function onSubmit(locationId: string) {
    if (isMultiLocation && locationId) {
      changeLocation({ locationId });
    }
    onRequestClose();
  }

  function handleRequestClose() {
    onRequestClose();
    revertAddress();
  }
  const footer = (
    <ModalLayoutBasicFooter
      isMobile={isMobile}
      onOkClick={handleOkClick}
      onCancelClick={() => {
        biLogger.report(closeDispatchSettings({ close_type: 'cancel' }));
        handleRequestClose();
      }}
      isLoading={isLoadingLocationFromServer}
      isSaveBtnDisabled={!isFormValid || isFetchingEstimateFromTPA}
      fullWidthMobile
    />
  );

  const className = classNames({
    [styles.hideModal]: isAddressSelectionModalOpen,
    'restaurants-online-orders-address-information-modal-ios': isIphone,
  });

  return (
    <ModalLayoutSelection
      scrollable
      header={t('order_settings_modal_title')}
      footer={footer}
      onCloseClick={() => {
        biLogger.report(closeDispatchSettings({ close_type: 'exit' }));
        handleRequestClose();
      }}
      data-hook={dataHooks.addressInformationModal}
      className={className}
    >
      {isLoadingLocationsSettingsFromServer ? (
        <div className={styles.spinnerWrapper}>
          <Spinner data-hook={dataHooks.loadingLocationsSettingsSpinner} />
        </div>
      ) : (
        <AddressInformation viewedFrom="multi-location-modal" ref={addressInformationRef} onSubmit={onSubmit} />
      )}
    </ModalLayoutSelection>
  );
};

AddressInformationModal.displayName = 'AddressInformationModal';

export default AddressInformationModal;
